/**
 * Canonical Application Geocoding Service
 * Provides verified coordinates, timezone offsets, and geocoding integration.
 */

export interface VerifiedLocation {
  name: string;
  placeName: string;
  latitude: number;
  longitude: number;
  timezoneOffsetMinutes: number;
  timezoneName?: string;
  country?: string;
}

// High-precision canonical Vedic astronomical and global reference locations
export const CANONICAL_CITY_PRESETS: VerifiedLocation[] = [
  // Vedic Astronomical & Spiritual Capitals (India)
  {
    name: 'Indore',
    placeName: 'Indore, Madhya Pradesh, India',
    latitude: 22.7196,
    longitude: 75.8577,
    timezoneOffsetMinutes: 330,
    timezoneName: 'IST (UTC+5:30)',
    country: 'India'
  },
  {
    name: 'Ujjain',
    placeName: 'Ujjain, Madhya Pradesh, India (Avanti - Prime Meridian of Vedic Astronomy)',
    latitude: 23.1765,
    longitude: 75.7885,
    timezoneOffsetMinutes: 330,
    timezoneName: 'IST (UTC+5:30)',
    country: 'India'
  },
  {
    name: 'New Delhi',
    placeName: 'New Delhi, Delhi, India',
    latitude: 28.6139,
    longitude: 77.2090,
    timezoneOffsetMinutes: 330,
    timezoneName: 'IST (UTC+5:30)',
    country: 'India'
  },
  {
    name: 'Varanasi',
    placeName: 'Varanasi (Kashi), Uttar Pradesh, India',
    latitude: 25.3176,
    longitude: 82.9739,
    timezoneOffsetMinutes: 330,
    timezoneName: 'IST (UTC+5:30)',
    country: 'India'
  },
  {
    name: 'Prayagraj',
    placeName: 'Prayagraj (Allahabad), Uttar Pradesh, India',
    latitude: 25.4358,
    longitude: 81.8463,
    timezoneOffsetMinutes: 330,
    timezoneName: 'IST (UTC+5:30)',
    country: 'India'
  },
  {
    name: 'Ayodhya',
    placeName: 'Ayodhya, Uttar Pradesh, India',
    latitude: 26.7922,
    longitude: 82.1998,
    timezoneOffsetMinutes: 330,
    timezoneName: 'IST (UTC+5:30)',
    country: 'India'
  },
  {
    name: 'Haridwar',
    placeName: 'Haridwar, Uttarakhand, India',
    latitude: 29.9457,
    longitude: 78.1642,
    timezoneOffsetMinutes: 330,
    timezoneName: 'IST (UTC+5:30)',
    country: 'India'
  },
  {
    name: 'Mumbai',
    placeName: 'Mumbai, Maharashtra, India',
    latitude: 19.0760,
    longitude: 72.8777,
    timezoneOffsetMinutes: 330,
    timezoneName: 'IST (UTC+5:30)',
    country: 'India'
  },
  {
    name: 'Bengaluru',
    placeName: 'Bengaluru, Karnataka, India',
    latitude: 12.9716,
    longitude: 77.5946,
    timezoneOffsetMinutes: 330,
    timezoneName: 'IST (UTC+5:30)',
    country: 'India'
  },
  {
    name: 'Jaipur',
    placeName: 'Jaipur, Rajasthan, India',
    latitude: 26.9124,
    longitude: 75.7873,
    timezoneOffsetMinutes: 330,
    timezoneName: 'IST (UTC+5:30)',
    country: 'India'
  },
  {
    name: 'Kolkata',
    placeName: 'Kolkata, West Bengal, India',
    latitude: 22.5726,
    longitude: 88.3639,
    timezoneOffsetMinutes: 330,
    timezoneName: 'IST (UTC+5:30)',
    country: 'India'
  },
  {
    name: 'Chennai',
    placeName: 'Chennai, Tamil Nadu, India',
    latitude: 13.0827,
    longitude: 80.2707,
    timezoneOffsetMinutes: 330,
    timezoneName: 'IST (UTC+5:30)',
    country: 'India'
  },
  {
    name: 'Pune',
    placeName: 'Pune, Maharashtra, India',
    latitude: 18.5204,
    longitude: 73.8567,
    timezoneOffsetMinutes: 330,
    timezoneName: 'IST (UTC+5:30)',
    country: 'India'
  },
  {
    name: 'Ahmedabad',
    placeName: 'Ahmedabad, Gujarat, India',
    latitude: 23.0225,
    longitude: 72.5714,
    timezoneOffsetMinutes: 330,
    timezoneName: 'IST (UTC+5:30)',
    country: 'India'
  },
  {
    name: 'Amritsar',
    placeName: 'Amritsar, Punjab, India',
    latitude: 31.6340,
    longitude: 74.8723,
    timezoneOffsetMinutes: 330,
    timezoneName: 'IST (UTC+5:30)',
    country: 'India'
  },
  {
    name: 'Hyderabad',
    placeName: 'Hyderabad, Telangana, India',
    latitude: 17.3850,
    longitude: 78.4867,
    timezoneOffsetMinutes: 330,
    timezoneName: 'IST (UTC+5:30)',
    country: 'India'
  },
  {
    name: 'Patna',
    placeName: 'Patna, Bihar, India',
    latitude: 25.5941,
    longitude: 85.1376,
    timezoneOffsetMinutes: 330,
    timezoneName: 'IST (UTC+5:30)',
    country: 'India'
  },

  // Neighboring & Global International Presets
  {
    name: 'Kathmandu',
    placeName: 'Kathmandu, Bagmati, Nepal',
    latitude: 27.7172,
    longitude: 85.3240,
    timezoneOffsetMinutes: 345,
    timezoneName: 'NPT (UTC+5:45)',
    country: 'Nepal'
  },
  {
    name: 'Dubai',
    placeName: 'Dubai, United Arab Emirates',
    latitude: 25.2048,
    longitude: 55.2708,
    timezoneOffsetMinutes: 240,
    timezoneName: 'GST (UTC+4)',
    country: 'United Arab Emirates'
  },
  {
    name: 'London',
    placeName: 'London, England, United Kingdom',
    latitude: 51.5074,
    longitude: -0.1278,
    timezoneOffsetMinutes: 0,
    timezoneName: 'GMT/BST (UTC+0)',
    country: 'United Kingdom'
  },
  {
    name: 'New York',
    placeName: 'New York, NY, United States',
    latitude: 40.7128,
    longitude: -74.0060,
    timezoneOffsetMinutes: -300,
    timezoneName: 'EST/EDT (UTC-5)',
    country: 'United States'
  },
  {
    name: 'San Francisco',
    placeName: 'San Francisco, CA, United States',
    latitude: 37.7749,
    longitude: -122.4194,
    timezoneOffsetMinutes: -480,
    timezoneName: 'PST/PDT (UTC-8)',
    country: 'United States'
  },
  {
    name: 'Toronto',
    placeName: 'Toronto, Ontario, Canada',
    latitude: 43.6532,
    longitude: -79.3832,
    timezoneOffsetMinutes: -300,
    timezoneName: 'EST/EDT (UTC-5)',
    country: 'Canada'
  },
  {
    name: 'Singapore',
    placeName: 'Singapore',
    latitude: 1.3521,
    longitude: 103.8198,
    timezoneOffsetMinutes: 480,
    timezoneName: 'SGT (UTC+8)',
    country: 'Singapore'
  },
  {
    name: 'Sydney',
    placeName: 'Sydney, New South Wales, Australia',
    latitude: -33.8688,
    longitude: 151.2093,
    timezoneOffsetMinutes: 600,
    timezoneName: 'AEST (UTC+10)',
    country: 'Australia'
  },
  {
    name: 'Tokyo',
    placeName: 'Tokyo, Japan',
    latitude: 35.6762,
    longitude: 139.6503,
    timezoneOffsetMinutes: 540,
    timezoneName: 'JST (UTC+9)',
    country: 'Japan'
  },
  {
    name: 'Frankfurt',
    placeName: 'Frankfurt, Hesse, Germany',
    latitude: 50.1109,
    longitude: 8.6821,
    timezoneOffsetMinutes: 60,
    timezoneName: 'CET/CEST (UTC+1)',
    country: 'Germany'
  }
];

export interface TimezoneOption {
  label: string;
  offsetMinutes: number;
  code: string;
  representativeRegions: string;
}

export const COMMON_TIMEZONES: TimezoneOption[] = [
  { label: 'UTC-10:00 (Hawaii)', offsetMinutes: -600, code: 'HST', representativeRegions: 'Hawaii, Honolulu' },
  { label: 'UTC-08:00 (Pacific Time - US/Canada)', offsetMinutes: -480, code: 'PST', representativeRegions: 'Los Angeles, San Francisco, Vancouver' },
  { label: 'UTC-07:00 (Mountain Time - US/Canada)', offsetMinutes: -420, code: 'MST', representativeRegions: 'Denver, Phoenix, Calgary' },
  { label: 'UTC-06:00 (Central Time - US/Canada)', offsetMinutes: -360, code: 'CST', representativeRegions: 'Chicago, Dallas, Mexico City' },
  { label: 'UTC-05:00 (Eastern Time - US/Canada)', offsetMinutes: -300, code: 'EST', representativeRegions: 'New York, Toronto, Miami, Bogota' },
  { label: 'UTC-04:00 (Atlantic Time)', offsetMinutes: -240, code: 'AST', representativeRegions: 'Halifax, Santiago, Caracas' },
  { label: 'UTC-03:00 (Brazil / Argentina)', offsetMinutes: -180, code: 'BRT', representativeRegions: 'Sao Paulo, Buenos Aires' },
  { label: 'UTC+00:00 (GMT / Western Europe)', offsetMinutes: 0, code: 'GMT', representativeRegions: 'London, Dublin, Lisbon, Accra' },
  { label: 'UTC+01:00 (Central European Time)', offsetMinutes: 60, code: 'CET', representativeRegions: 'Paris, Berlin, Rome, Madrid, Frankfurt' },
  { label: 'UTC+02:00 (Eastern European / South Africa)', offsetMinutes: 120, code: 'EET', representativeRegions: 'Athens, Cairo, Johannesburg, Kyiv' },
  { label: 'UTC+03:00 (Arabia / Moscow / East Africa)', offsetMinutes: 180, code: 'AST', representativeRegions: 'Riyadh, Moscow, Nairobi, Doha' },
  { label: 'UTC+03:30 (Iran Standard Time)', offsetMinutes: 210, code: 'IRST', representativeRegions: 'Tehran' },
  { label: 'UTC+04:00 (Gulf Standard Time - UAE)', offsetMinutes: 240, code: 'GST', representativeRegions: 'Dubai, Abu Dhabi, Muscat' },
  { label: 'UTC+04:30 (Afghanistan)', offsetMinutes: 270, code: 'AFT', representativeRegions: 'Kabul' },
  { label: 'UTC+05:00 (Pakistan / West Asia)', offsetMinutes: 300, code: 'PKT', representativeRegions: 'Karachi, Islamabad, Tashkent' },
  { label: 'UTC+05:30 (Indian Standard Time - IST)', offsetMinutes: 330, code: 'IST', representativeRegions: 'Indore, Delhi, Mumbai, All India, Colombo' },
  { label: 'UTC+05:45 (Nepal Time)', offsetMinutes: 345, code: 'NPT', representativeRegions: 'Kathmandu' },
  { label: 'UTC+06:00 (Bangladesh / Bhutan)', offsetMinutes: 360, code: 'BST', representativeRegions: 'Dhaka, Thimphu, Almaty' },
  { label: 'UTC+06:30 (Myanmar Time)', offsetMinutes: 390, code: 'MMT', representativeRegions: 'Yangon' },
  { label: 'UTC+07:00 (Indochina / Thailand)', offsetMinutes: 420, code: 'ICT', representativeRegions: 'Bangkok, Jakarta, Hanoi' },
  { label: 'UTC+08:00 (Singapore / China / WA)', offsetMinutes: 480, code: 'SGT', representativeRegions: 'Singapore, Beijing, Hong Kong, Perth' },
  { label: 'UTC+09:00 (Japan / Korea Standard Time)', offsetMinutes: 540, code: 'JST', representativeRegions: 'Tokyo, Seoul' },
  { label: 'UTC+09:30 (Australian Central Time)', offsetMinutes: 570, code: 'ACST', representativeRegions: 'Adelaide, Darwin' },
  { label: 'UTC+10:00 (Australian Eastern Time)', offsetMinutes: 600, code: 'AEST', representativeRegions: 'Sydney, Melbourne, Brisbane' },
  { label: 'UTC+11:00 (Solomon Islands)', offsetMinutes: 660, code: 'SBT', representativeRegions: 'Noumea, Honiara' },
  { label: 'UTC+12:00 (New Zealand Standard Time)', offsetMinutes: 720, code: 'NZST', representativeRegions: 'Auckland, Wellington, Fiji' }
];

const USER_LOCATION_STORAGE_KEY = 'astro_user_location_pref';

/**
 * Get user configured location preference from localStorage or fallback to Indore
 */
export function getUserLocationPref(): VerifiedLocation {
  try {
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      const raw = localStorage.getItem(USER_LOCATION_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed.latitude === 'number' && typeof parsed.longitude === 'number') {
          return parsed;
        }
      }
    }
  } catch (e) {
    console.warn('[geoService] Failed to load user location pref:', e);
  }
  return CANONICAL_CITY_PRESETS[0]; // Indore default
}

/**
 * Set and broadcast user configured location preference across the app
 */
export function setUserLocationPref(loc: VerifiedLocation): void {
  try {
    localStorage.setItem(USER_LOCATION_STORAGE_KEY, JSON.stringify(loc));
    // Broadcast custom event so all open widgets/drawers/calculators update synchronously
    window.dispatchEvent(new CustomEvent('astro_location_pref_changed', { detail: loc }));
  } catch (e) {
    console.warn('[geoService] Failed to save user location pref:', e);
  }
}

/**
 * Format verified coordinates with timezone indicator
 * e.g., "22.72° N, 75.86° E • IST (UTC+5:30)"
 */
export function formatVerifiedCoordinates(
  lat: number,
  lng: number,
  timezoneOffsetMinutes: number = 330
): string {
  const latDir = lat >= 0 ? 'N' : 'S';
  const lngDir = lng >= 0 ? 'E' : 'W';
  const absLat = Math.abs(lat).toFixed(2);
  const absLng = Math.abs(lng).toFixed(2);

  const sign = timezoneOffsetMinutes >= 0 ? '+' : '-';
  const absMins = Math.abs(timezoneOffsetMinutes);
  const hrs = Math.floor(absMins / 60);
  const mins = absMins % 60;
  const tzStr = `UTC${sign}${hrs}${mins > 0 ? `:${mins.toString().padStart(2, '0')}` : ':00'}`;

  const tzLabel = timezoneOffsetMinutes === 330 ? 'IST' : 'Local';

  return `${absLat}° ${latDir}, ${absLng}° ${lngDir} • ${tzLabel} (${tzStr})`;
}

/**
 * Resolves a city name to a verified location from presets or canonical match
 */
export function resolveLocationFromPreset(query?: string): VerifiedLocation {
  const userPref = getUserLocationPref();
  if (!query || query.trim() === '') {
    return userPref;
  }
  const clean = query.toLowerCase().trim();
  if (userPref.name.toLowerCase() === clean || userPref.placeName.toLowerCase().includes(clean)) {
    return userPref;
  }
  const matched = CANONICAL_CITY_PRESETS.find(
    (p) => p.name.toLowerCase() === clean || clean.includes(p.name.toLowerCase()) || p.placeName.toLowerCase().includes(clean)
  );

  return matched || userPref;
}

/**
 * Searches the internal geocoding service (/api/geocoding) with fallback to canonical presets
 */
export async function searchCanonicalLocations(query: string): Promise<VerifiedLocation[]> {
  const clean = (query || '').trim();
  if (!clean || clean.length < 2) {
    return CANONICAL_CITY_PRESETS.slice(0, 6);
  }

  // Check matching canonical presets first
  const presetMatches = CANONICAL_CITY_PRESETS.filter(
    (p) =>
      p.name.toLowerCase().includes(clean.toLowerCase()) ||
      p.placeName.toLowerCase().includes(clean.toLowerCase())
  );

  try {
    const res = await fetch(`/api/geocoding?q=${encodeURIComponent(clean)}`);
    if (res.ok) {
      const data = await res.json();
      if (Array.isArray(data) && data.length > 0) {
        const apiResults: VerifiedLocation[] = data.map((item: any) => ({
          name: item.name || item.display_name?.split(',')[0] || clean,
          placeName: item.display_name || item.name || clean,
          latitude: typeof item.latitude === 'number' ? item.latitude : parseFloat(item.latitude),
          longitude: typeof item.longitude === 'number' ? item.longitude : parseFloat(item.longitude),
          timezoneOffsetMinutes: item.timezoneOffsetMinutes || 330,
          timezoneName: item.timezoneOffsetMinutes === 330 ? 'IST' : 'UTC',
          country: item.country || ''
        }));

        // Merge deduplicating by placeName
        const combined = [...presetMatches];
        for (const r of apiResults) {
          if (!combined.some((c) => Math.abs(c.latitude - r.latitude) < 0.05 && Math.abs(c.longitude - r.longitude) < 0.05)) {
            combined.push(r);
          }
        }
        return combined.slice(0, 10);
      }
    }
  } catch (err) {
    console.warn('[geoService] API search failed, falling back to canonical presets:', err);
  }

  return presetMatches.length > 0 ? presetMatches : CANONICAL_CITY_PRESETS.slice(0, 6);
}
